import React, { useState } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  X, 
  CreditCard, 
  DollarSign, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Lock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initialise Stripe once at module level (not inside a component)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card element visual options — matches JOP input style
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#374151',
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': { color: '#9CA3AF' },
    },
    invalid: {
      color: '#EF4444',
      iconColor: '#EF4444',
    },
  },
};

// ─── Inner form — must live inside <Elements> to use useStripe / useElements ───
const StripePaymentForm = ({ clientSecret, jobId, commissionBreakdown, isFundingOnBehalf, originatorBusiness, onSuccess, onClose, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setLoading(true);
    setCardError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (error) {
        // Stripe card error (declined, invalid, etc.)
        setCardError(error.message);
        toast.error(error.message);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Notify backend to mark the job as funded
        await axios.post(`${API}/payments/confirm-funding`, {
          job_id: jobId,
          payment_intent_id: paymentIntent.id,
        });

        toast.success('Payment successful! Job is now funded and in escrow.');
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error('Payment error:', err);
      const msg = err.response?.data?.detail || 'Payment failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {commissionBreakdown && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Commission Breakdown</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Job Quote:</span>
              <span className="font-medium">${commissionBreakdown.originator_amount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Receiver Net:</span>
              <span className="font-medium text-green-600">${commissionBreakdown.receiver_amount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Platform Fee:</span>
              <span className="font-medium">${commissionBreakdown.platform_fee}</span>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="font-medium">Total to Charge:</span>
              <span className="font-bold">${commissionBreakdown.originator_amount}</span>
            </div>
          </div>
        </div>
      )}

      {isFundingOnBehalf && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
          Admin override: charging payment on behalf of {originatorBusiness}
        </div>
      )}

      {/* Real Stripe CardElement */}
      <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white">
        <div className="flex items-center mb-3">
          <CreditCard className="h-5 w-5 text-gray-500 mr-2" />
          <span className="text-sm font-medium text-gray-700">Card Details</span>
          <div className="ml-auto flex items-center text-xs text-gray-400">
            <Lock className="h-3 w-3 mr-1" />
            Secured by Stripe
          </div>
        </div>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {/* Card error message */}
      {cardError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start">
          <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
          {cardError}
        </div>
      )}

      <div className="flex space-x-4">
        <button
          onClick={handleSubmit}
          disabled={loading || !stripe}
          className="jop-button flex-1 flex items-center justify-center"
        >
          {loading ? (
            <div className="spinner" />
          ) : (
            <>
              <Shield className="h-4 w-4 mr-2" />
              Pay & Fund Job
            </>
          )}
        </button>
        <button onClick={onBack} className="jop-button-secondary">
          Back
        </button>
      </div>

      <p className="text-xs text-gray-400 text-center mt-3">
        Funds are held in escrow and released to the receiver on job completion.
      </p>
    </>
  );
};

// ─── Outer modal ───────────────────────────────────────────────────────────────
const JobFundingModal = ({ job, isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [commissionBreakdown, setCommissionBreakdown] = useState(null);
  const [adminConfirmed, setAdminConfirmed] = useState(false);

  const isAdmin = user?.is_admin;
  const isOriginator = user?.id === job?.originator_id;
  const isFundingOnBehalf = isAdmin && !isOriginator;

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/payments/create-intent`, {
        job_id: job.id,
        amount: job.quoted_price,
      });
      setPaymentIntent(response.data);
      setCommissionBreakdown(response.data.commission_breakdown);
    } catch (error) {
      console.error('Failed to create payment intent:', error);
      toast.error('Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setPaymentIntent(null);
    setCommissionBreakdown(null);
  };

  if (!isOpen || !job) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Shield className="h-6 w-6 text-blue-600 mr-2" />
                {isFundingOnBehalf ? 'Fund Job on Behalf of Originator' : 'Fund Job with Secure Escrow'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isFundingOnBehalf
                  ? `Fund this job on behalf of ${job.originator_business}`
                  : 'Secure your job with escrow payment to ensure completion'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Step 1: Overview */}
          {!paymentIntent && (
            <>
              {/* Admin confirmation gate */}
              {isFundingOnBehalf && !adminConfirmed ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-900 mb-2">Admin Funding Confirmation</h4>
                      <p className="text-sm text-amber-700 mb-4">
                        You are about to fund this job on behalf of <strong>{job.originator_business}</strong>.
                      </p>
                      <div className="flex space-x-3">
                        <button onClick={() => setAdminConfirmed(true)} className="jop-button-secondary">
                          Yes, Fund on Behalf
                        </button>
                        <button onClick={onClose} className="jop-button-secondary">
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-900 mb-2">Secure Escrow Protection</h4>
                        <div className="text-sm text-blue-700 space-y-1">
                          <p>• Funds are held securely until job completion</p>
                          <p>• Payment released automatically when job is verified complete</p>
                          <p>• Full refund if job is not delivered as specified</p>
                          <p>• Transparent commission structure with no hidden fees</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg mb-6">
                    <span className="font-medium text-gray-900">Job Quote:</span>
                    <span className="text-xl font-bold text-gray-900">${job.quoted_price}</span>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={createPaymentIntent}
                      disabled={loading}
                      className="jop-button flex-1 flex items-center justify-center"
                    >
                      {loading ? (
                        <div className="spinner" />
                      ) : (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          {isFundingOnBehalf ? 'Fund on Behalf of Originator' : 'Continue to Payment'}
                        </>
                      )}
                    </button>
                    <button onClick={onClose} className="jop-button-secondary">
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Step 2: Real Stripe card form */}
          {paymentIntent && (
            <Elements stripe={stripePromise}>
              <StripePaymentForm
                clientSecret={paymentIntent.client_secret}
                jobId={job.id}
                commissionBreakdown={commissionBreakdown}
                isFundingOnBehalf={isFundingOnBehalf}
                originatorBusiness={job.originator_business}
                onSuccess={onSuccess}
                onClose={onClose}
                onBack={handleBack}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobFundingModal;
